import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../../api/client';
import type { Contact, ContactType } from '../../../api/types';
import { CONTACT_ICONS, renderContactIcon } from '../../../data/contactIcons';

const EMPTY: Partial<Contact> = {
    label: '',
    value: '',
    type: 'email',
    icon: 'envelope',
    sortOrder: 0,
};

export function ContactsPanel() {
    const navigate = useNavigate();
    const location = useLocation();
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [editing, setEditing] = useState<Partial<Contact> | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = () =>
        api.contacts.list().then(setContacts).catch((e) => setError(e.message));

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        const m = location.pathname.match(/^\/admin\/contacts\/(new|\d+)$/);
        if (!m) {
            setEditing(null);
            return;
        }
        if (m[1] === 'new') {
            setEditing(EMPTY);
            return;
        }
        const id = Number(m[1]);
        const c = contacts.find((x) => x.id === id);
        if (c) setEditing(c);
    }, [location.pathname, contacts]);

    const openEditor = (c: Contact) => navigate(`/admin/contacts/${c.id}`);
    const openNewEditor = () => navigate('/admin/contacts/new');
    const closeEditor = () => navigate('/admin');

    const save = async () => {
        if (!editing) return;
        setBusy(true);
        setError(null);
        try {
            if (editing.id) {
                await api.contacts.update(editing.id, editing);
            } else {
                await api.contacts.create(editing);
            }
            await load();
            closeEditor();
        } catch (e: any) {
            setError(e.message ?? 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!editing?.id) return;
        if (!window.confirm(`Delete "${editing.label || editing.value}"?`)) return;
        setBusy(true);
        try {
            await api.contacts.remove(editing.id);
            await load();
            closeEditor();
        } catch (e: any) {
            setError(e.message ?? 'Delete failed');
        } finally {
            setBusy(false);
        }
    };

    if (editing) {
        const isEmail = (editing.type ?? 'email') === 'email';
        return (
            <div className="admin-form">
                <h2>{editing.id ? 'Edit contact' : 'New contact'}</h2>
                <label>
                    <span>Type</span>
                    <select
                        value={editing.type ?? 'email'}
                        onChange={(e) =>
                            setEditing({
                                ...editing,
                                type: e.target.value as ContactType,
                                icon:
                                    e.target.value === 'email'
                                        ? 'envelope'
                                        : editing.icon || 'instagram',
                            })
                        }
                    >
                        <option value="email">email</option>
                        <option value="social">social / link</option>
                    </select>
                </label>
                <label>
                    <span>
                        Label{' '}
                        <span className="admin-field-hint">
                            {isEmail
                                ? '(e.g. "Booking", "Manager" — shown next to the email)'
                                : '(internal name, not shown on the site)'}
                        </span>
                    </span>
                    <input
                        value={editing.label ?? ''}
                        onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    />
                </label>
                <label>
                    <span>{isEmail ? 'Email address' : 'URL'}</span>
                    <input
                        type={isEmail ? 'email' : 'url'}
                        value={editing.value ?? ''}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        placeholder={isEmail ? 'name@example.com' : 'https://…'}
                    />
                </label>
                <label>
                    <span>Icon</span>
                    <select
                        value={editing.icon ?? ''}
                        onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                    >
                        {CONTACT_ICONS.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    <span>Sort order (lower = first)</span>
                    <input
                        type="number"
                        value={editing.sortOrder ?? 0}
                        onChange={(e) =>
                            setEditing({ ...editing, sortOrder: Number(e.target.value) })
                        }
                    />
                </label>
                {error && <div className="admin-error">{error}</div>}
                <div className="admin-form-actions">
                    <button className="admin-primary" onClick={save} disabled={busy}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={closeEditor} disabled={busy}>
                        Cancel
                    </button>
                    {editing.id && (
                        <button className="admin-danger" onClick={remove} disabled={busy}>
                            Delete
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="admin-list">
            <div className="admin-list-head">
                <h2>Contacts ({contacts.length})</h2>
                <button onClick={openNewEditor}>+ New contact</button>
            </div>
            {error && <div className="admin-error">{error}</div>}
            <ul>
                {contacts.map((c) => (
                    <li key={c.id}>
                        <button className="admin-list-row" onClick={() => openEditor(c)}>
                            <span className="admin-list-title">
                                <span className="admin-contact-icon">
                                    {renderContactIcon(c.icon)}
                                </span>
                                {c.label || c.value}
                            </span>
                            <span className="admin-list-meta">
                                {c.type} · {c.value}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
